import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, X, ArrowLeftRight } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { LOCATIONS } from '@/components/ui/location-autocomplete';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

export interface CityStop {
  id: string;
  destination: string;
  country: string;
  arrivalDate?: Date;
  departureDate?: Date;
}

interface MultiCitySelectorProps {
  cities: CityStop[];
  onCitiesChange: (cities: CityStop[]) => void;
  tripStartDate?: Date;
  tripEndDate?: Date;
}

export function MultiCitySelector({ cities, onCitiesChange, tripStartDate, tripEndDate }: MultiCitySelectorProps) {
  const [showSuggestions, setShowSuggestions] = useState<Record<string, boolean>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const addCity = () => {
    const newCity: CityStop = {
      id: Date.now().toString(),
      destination: '',
      country: '',
    };
    onCitiesChange([...cities, newCity]);
  };

  const removeCity = (id: string) => {
    onCitiesChange(cities.filter(city => city.id !== id));
  };

  const updateCity = (id: string, updates: Partial<CityStop>) => {
    onCitiesChange(cities.map(city => 
      city.id === id ? { ...city, ...updates } : city
    ));
  };

  const swapCities = (index: number) => {
    if (index < 0 || index >= cities.length - 1) return;
    const newCities = [...cities];
    [newCities[index], newCities[index + 1]] = [newCities[index + 1], newCities[index]];
    onCitiesChange(newCities);
  };

  const handleDestinationChange = (cityId: string, value: string) => {
    setInputValues(prev => ({ ...prev, [cityId]: value }));
    setShowSuggestions(prev => ({ ...prev, [cityId]: value.trim().length > 0 }));
    
    if (!value.trim()) {
      updateCity(cityId, { destination: '', country: '' });
    }
  };

  const selectDestination = (cityId: string, location: { name: string; country: string }) => {
    updateCity(cityId, { destination: location.name, country: location.country });
    setInputValues(prev => ({ ...prev, [cityId]: `${location.name}, ${location.country}` }));
    setShowSuggestions(prev => ({ ...prev, [cityId]: false }));
  };

  const getFilteredDestinations = (searchTerm: string) => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase().trim();
    return LOCATIONS.filter(location =>
      location.name.toLowerCase().includes(term) ||
      location.country.toLowerCase().includes(term)
    ).slice(0, 8);
  };

  return (
    <div className="space-y-3">
      {cities.map((city, index) => {
        const isLast = index === cities.length - 1;
        const inputValue = inputValues[city.id] || 
          (city.destination ? `${city.destination}, ${city.country}` : '');
        const filteredDestinations = getFilteredDestinations(inputValue);

        return (
          <div key={city.id}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start p-4 rounded-lg bg-background border-2 border-border">
              {/* From/To Column */}
              <div className="md:col-span-5 space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {index === 0 ? 'From' : 'To'}
                </Label>
                <div className="relative">
                  <Input
                    placeholder="City or airport"
                    value={inputValue}
                    onChange={(e) => handleDestinationChange(city.id, e.target.value)}
                    onFocus={() => inputValue.trim() && setShowSuggestions(prev => ({ ...prev, [city.id]: true }))}
                    className="h-12"
                  />
                  {showSuggestions[city.id] && filteredDestinations.length > 0 && (
                    <div className="absolute z-50 w-full mt-1">
                      <Command className="rounded-lg border shadow-md">
                        <CommandList>
                          <CommandGroup>
                            {filteredDestinations.map((location) => (
                              <CommandItem
                                key={`${location.name}-${location.country}`}
                                onSelect={() => selectDestination(city.id, location)}
                                className="cursor-pointer"
                              >
                                {location.name}, {location.country}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </div>
                  )}
                </div>
              </div>

              {/* Swap Button - Only show between cities */}
              {!isLast && (
                <div className="md:col-span-1 flex items-center justify-center md:mt-7">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => swapCities(index)}
                    className="rounded-full h-10 w-10 bg-primary/10 hover:bg-primary/20"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Depart Date Column */}
              <div className={cn("space-y-2", isLast ? "md:col-span-5" : "md:col-span-4")}>
                <Label className="text-xs text-muted-foreground">Depart</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full h-12 justify-start text-left font-normal',
                        !city.departureDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {city.departureDate ? format(city.departureDate, 'MMM dd') : 'Add date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={city.departureDate}
                      onSelect={(date) => updateCity(city.id, { departureDate: date })}
                      disabled={(date) => {
                        if (tripStartDate && date < tripStartDate) return true;
                        if (tripEndDate && date > tripEndDate) return true;
                        return false;
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Remove Button */}
              <div className={cn("flex items-center md:mt-7", isLast ? "md:col-span-2" : "md:col-span-2")}>
                {cities.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCity(city.id)}
                    className="h-10 w-10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Connecting line between cities */}
            {!isLast && (
              <div className="flex justify-center py-1">
                <div className="w-0.5 h-6 bg-border" />
              </div>
            )}
          </div>
        );
      })}

      <Button
        type="button"
        variant="outline"
        onClick={addCity}
        className="w-full h-12 border-dashed"
      >
        <Plus className="mr-2 h-4 w-4" />
        Add another destination
      </Button>
    </div>
  );
}
